// SOVRANOVA — Handover brief. A consent-first WELLNESS summary the user can
// review and hand to their own therapist / counsellor / GP. User-initiated;
// draws only on their own data; never a diagnosis. The on-screen preview is the
// SAME document that prints to PDF (rendered in an isolated iframe; browser
// "Save as PDF" — zero dependencies, no style leakage).
import { useState } from 'react'
import { api } from '../../services/api'
import { resolveRegion } from '../../services/crisisResources'

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

function buildHtml(b) {
  const g = b.atAGlance || {}, mood = b.moodTrend || {}, crisis = b.crisisLog || {}
  const scoreLine = (arr, label) => (arr && arr.length)
    ? `<p class="kv"><b>${label}</b> ${arr.map(x => `${esc(x.date)}: ${esc(String(x.score))}${x.severity ? ' (' + esc(x.severity) + ')' : ''}`).join(' &rarr; ')}</p>` : ''
  const narr = esc(b.narrative).split(/\n+/).filter(Boolean).map(p => `<p>${p}</p>`).join('')
  const areas = (g.topAreas && g.topAreas.length) ? esc(g.topAreas.join(', ')) : '—'
  const range = (g.firstDate && g.lastDate) ? `${esc(g.firstDate)} – ${esc(g.lastDate)}` : '—'
  const excerpts = (b.excerpts || []).length
    ? b.excerpts.map(e => `<blockquote>&ldquo;${esc(e.text)}&rdquo;<span class="cite">${esc(e.date)}</span></blockquote>`).join('')
    : '<p class="muted">No excerpts selected.</p>'
  const crisisBlock = crisis.count
    ? `<ul>${crisis.events.map(c => `<li>${esc(c.date)} — support resources shown (level: ${esc(c.riskLevel)})</li>`).join('')}</ul><p class="muted">${esc(crisis.note)}</p>`
    : `<p class="muted">${esc(crisis.note)}</p>`
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Wellness summary — ${esc(b.name)}</title>
<style>
  *{box-sizing:border-box} body{font-family:Georgia,'Times New Roman',serif;color:#1d1d23;background:#fff;margin:0;padding:30px;line-height:1.55}
  .wrap{max-width:720px;margin:0 auto}
  h1{font-size:22px;margin:0 0 2px} .sub{color:#666;font-size:12px;margin:0 0 18px}
  h2{font-size:13px;letter-spacing:.05em;text-transform:uppercase;color:#7a5cc0;border-bottom:1px solid #e6e0f0;padding-bottom:4px;margin:22px 0 8px}
  p{margin:.45rem 0;font-size:14px} .kv{font-size:13px} .muted{color:#777;font-size:12.5px}
  ul{font-size:13.5px;padding-left:18px} li{margin:.2rem 0}
  blockquote{margin:.5rem 0;padding:.4rem .85rem;border-left:3px solid #c9a84c;font-style:italic;color:#33333a;font-size:13.5px}
  .cite{display:block;font-style:normal;font-size:11px;color:#999;margin-top:3px}
  .glance{display:flex;flex-wrap:wrap;gap:10px;font-size:12.5px;margin-bottom:6px} .glance div{background:#f6f4fb;border-radius:8px;padding:7px 11px}
  .disc{margin-top:24px;padding:12px 14px;background:#faf7ef;border:1px solid #e8dcbf;border-radius:8px;font-size:11.5px;color:#5b513a;line-height:1.5}
  @media print{body{padding:0}}
</style></head><body><div class="wrap">
  <h1>Wellness summary — ${esc(b.name)}</h1>
  <p class="sub">Prepared by ${esc(b.name)} in SOVRANOVA · ${esc(b.generatedAt)} · self-reported — not a clinical assessment</p>
  <div class="glance"><div><b>${esc(String(g.sessions || 0))}</b> sessions</div><div>Engagement: ${esc(g.depth || '—')}</div><div>Period: ${range}</div><div>Focus: ${areas}</div></div>
  <h2>Summary</h2>${narr || '<p class="muted">No summary available.</p>'}
  <h2>Self-reported check-ins</h2>${scoreLine(mood.phq9, 'PHQ-9 (low mood):')}${scoreLine(mood.gad7, 'GAD-7 (anxiety):')}<p class="muted">${esc(mood.note)}</p>
  <h2>In ${esc(b.name)}&rsquo;s own words</h2>${excerpts}
  <h2>Safety record</h2>${crisisBlock}
  <div class="disc">${esc(b.disclaimer)}</div>
</div></body></html>`
}

// Load jsPDF on demand (injected CDN script — not bundled; only fetched when the
// user taps Download). The app runs without CSP restrictions, so this is allowed.
function ensureJsPdf() {
  if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const cur = document.getElementById('jspdf-cdn')
    if (cur) { cur.addEventListener('load', () => resolve()); cur.addEventListener('error', reject); return }
    const s = document.createElement('script')
    s.id = 'jspdf-cdn'
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.2/jspdf.umd.min.js'
    s.onload = () => resolve(); s.onerror = reject
    document.head.appendChild(s)
  })
}

// Lay out the brief as a real, selectable-text PDF (mirrors the on-screen document).
function buildPdf(b) {
  const { jsPDF } = window.jspdf
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const M = 48, W = doc.internal.pageSize.getWidth(), H = doc.internal.pageSize.getHeight()
  const maxW = W - M * 2
  let y = M
  const need = (h) => { if (y + h > H - M) { doc.addPage(); y = M } }
  const para = (str, size, rgb, gap, font) => {
    doc.setFont('helvetica', font || 'normal'); doc.setFontSize(size); doc.setTextColor(rgb[0], rgb[1], rgb[2])
    doc.splitTextToSize(String(str == null ? '' : str), maxW).forEach(ln => { need(size + 5); doc.text(ln, M, y); y += size + 5 })
    y += gap || 0
  }
  const heading = (str) => { need(28); doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(122, 92, 192); doc.text(str.toUpperCase(), M, y); y += 7; doc.setDrawColor(230, 224, 240); doc.line(M, y, W - M, y); y += 13 }
  const g = b.atAGlance || {}, mood = b.moodTrend || {}, crisis = b.crisisLog || {}
  para(`Wellness summary - ${b.name}`, 18, [29, 29, 35], 3, 'bold')
  para(`Prepared by ${b.name} in SOVRANOVA  ·  ${b.generatedAt}  ·  self-reported, not a clinical assessment`, 9, [102, 102, 102], 9)
  const range = (g.firstDate && g.lastDate) ? `${g.firstDate} - ${g.lastDate}` : '—'
  const areas = (g.topAreas && g.topAreas.length) ? g.topAreas.join(', ') : '—'
  para(`${g.sessions || 0} sessions    ·    Engagement: ${g.depth || '—'}    ·    Period: ${range}    ·    Focus: ${areas}`, 10, [51, 51, 51], 10)
  heading('Summary'); para(b.narrative || 'No summary available.', 11, [29, 29, 35], 8)
  heading('Self-reported check-ins')
  const sl = (arr, label) => { if (arr && arr.length) para(`${label} ` + arr.map(x => `${x.date}: ${x.score}${x.severity ? ' (' + x.severity + ')' : ''}`).join('    ·    '), 10, [29, 29, 35], 2) }
  sl(mood.phq9, 'PHQ-9 (low mood):'); sl(mood.gad7, 'GAD-7 (anxiety):'); para(mood.note || '', 9, [119, 119, 119], 8)
  heading(`In ${b.name}'s own words`)
  if ((b.excerpts || []).length) b.excerpts.forEach(e => { para(`"${e.text}"`, 10.5, [51, 51, 58], 1, 'italic'); para(e.date, 8, [153, 153, 153], 6) })
  else para('No excerpts selected.', 9, [119, 119, 119], 6)
  heading('Safety record')
  if (crisis.count) crisis.events.forEach(c => para(`-  ${c.date} — support resources shown (level: ${c.riskLevel})`, 10, [29, 29, 35], 1))
  para(crisis.note || '', 9, [119, 119, 119], 12)
  const dl = doc.splitTextToSize(b.disclaimer || '', maxW - 16)
  const boxH = dl.length * 12 + 16
  need(boxH); doc.setFillColor(250, 247, 239); doc.setDrawColor(232, 220, 191); doc.rect(M, y, maxW, boxH, 'FD')
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(91, 81, 58)
  let yy = y + 14; dl.forEach(ln => { doc.text(ln, M + 8, yy); yy += 12 }); y += boxH
  return doc
}

export function HandoverView({ onClose }) {
  const [stage, setStage] = useState('intro')
  const [brief, setBrief] = useState(null)
  const [error, setError] = useState(null)
  const [pdfBusy, setPdfBusy] = useState(false)

  const generate = async () => {
    setStage('loading'); setError(null)
    try {
      const r = await api.post('/handover/generate', { region: resolveRegion() })
      if (r && r.brief) { setBrief(r.brief); setStage('ready') }
      else { setError('Could not build the summary.'); setStage('error') }
    } catch (e) {
      setError((e && e.message) || 'Something went wrong.'); setStage('error')
    }
  }

  const savePdf = () => {
    if (!brief) return
    const w = window.open('', '_blank')
    if (!w) { alert('Please allow pop-ups to save the PDF.'); return }
    w.document.write(buildHtml(brief)); w.document.close(); w.focus()
    setTimeout(() => { try { w.print() } catch (_) {} }, 500)
  }

  const downloadPdf = async () => {
    if (!brief) return
    setPdfBusy(true)
    try {
      await ensureJsPdf()
      buildPdf(brief).save(`SOVRANOVA wellness summary - ${brief.name}.pdf`)
    } catch (_) { savePdf() }
    finally { setPdfBusy(false) }
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <button style={S.back} onClick={onClose}>← Back</button>
        <div style={S.title}>Summary for a professional</div>
        <div style={{ width: 56 }} />
      </div>
      <div style={S.body}>
        {stage === 'intro' && (
          <div style={S.card}>
            <p style={S.lead}>Create a private summary to share with your therapist, counsellor, or GP.</p>
            <p style={S.para}>It gathers what you've shared and tracked in SOVRANOVA — themes, your self-reported check-ins, what's helped, a few of your own words, and any times support was shown — into one clear document.</p>
            <ul style={S.points}>
              <li>It's a <b>wellness summary</b>, not a diagnosis or clinical assessment.</li>
              <li>It's built only from <b>your own</b> information.</li>
              {/* Was "Nothing is sent anywhere." — the summary is written on our
                  server by an AI provider from your own data. Nothing goes to
                  any clinician or third party, which is the real promise. */}
              <li><b>It isn't shared with anyone.</b> SOVRANOVA writes it from your own data; you review it first and choose what to do with it.</li>
            </ul>
            <button style={S.primary} onClick={generate}>Generate my summary</button>
          </div>
        )}
        {stage === 'loading' && (
          <div style={S.center}><div style={S.spin}>◎</div><p style={S.muted}>Putting your summary together…</p></div>
        )}
        {stage === 'error' && (
          <div style={S.card}><p style={S.lead}>Couldn't generate it just now.</p><p style={S.muted}>{error}</p><button style={S.primary} onClick={generate}>Try again</button></div>
        )}
        {stage === 'ready' && brief && (
          <>
            <div style={S.actions}>
              <button style={S.primary} onClick={downloadPdf} disabled={pdfBusy}>{pdfBusy ? 'Preparing…' : 'Download PDF'}</button>
              <button style={S.ghost} onClick={savePdf}>Print</button>
              <button style={S.ghost} onClick={generate}>Regenerate</button>
            </div>
            <iframe title="Wellness summary preview" srcDoc={buildHtml(brief)} style={S.frame}
              onLoad={(e) => { try { e.target.style.height = (e.target.contentWindow.document.body.scrollHeight + 28) + 'px' } catch (_) {} }} />
            <p style={S.hint}>"Download PDF" saves a file you can share with your professional. "Print" opens your browser's print view if you prefer.</p>
          </>
        )}
      </div>
    </div>
  )
}

const S = {
  page:    { display:'flex', flexDirection:'column', minHeight:'100vh', background:'linear-gradient(170deg,#1a0a2e,#0f0620)' },
  header:  { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.9rem 1.25rem', borderBottom:'1px solid rgba(255,255,255,.07)', background:'rgba(15,6,32,.97)', position:'sticky', top:0, zIndex:2 },
  back:    { background:'transparent', border:'none', color:'rgba(255,255,255,.55)', fontSize:'0.9rem', cursor:'pointer' },
  title:   { fontSize:'0.9rem', fontWeight:700, color:'#f5f0e8', letterSpacing:'0.02em' },
  body:    { flex:1, overflowY:'auto', padding:'1.25rem', maxWidth:760, width:'100%', margin:'0 auto' },
  card:    { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'1rem', padding:'1.4rem 1.5rem' },
  lead:    { fontSize:'1.1rem', fontWeight:600, color:'#f5f0e8', margin:'0 0 0.6rem', lineHeight:1.4 },
  para:    { fontSize:'0.92rem', color:'rgba(245,240,232,.7)', lineHeight:1.65, margin:'0 0 0.9rem' },
  points:  { margin:'0 0 1.2rem', padding:'0 0 0 1.1rem', color:'rgba(245,240,232,.72)', fontSize:'0.88rem', lineHeight:1.9 },
  primary: { padding:'0.85rem 1.5rem', borderRadius:'2rem', border:'none', background:'linear-gradient(135deg,#7b5cf0,#5a2d9a)', color:'#fff', fontSize:'0.98rem', fontWeight:600, cursor:'pointer', boxShadow:'0 4px 18px rgba(123,92,240,.35)' },
  ghost:   { padding:'0.85rem 1.25rem', borderRadius:'2rem', border:'1px solid rgba(255,255,255,.16)', background:'transparent', color:'rgba(245,240,232,.8)', fontSize:'0.95rem', fontWeight:600, cursor:'pointer' },
  actions: { display:'flex', gap:'0.75rem', marginBottom:'1rem', flexWrap:'wrap' },
  frame:   { width:'100%', minHeight:420, border:'none', borderRadius:'0.75rem', background:'#fff', boxShadow:'0 8px 30px rgba(0,0,0,.35)' },
  center:  { display:'flex', flexDirection:'column', alignItems:'center', gap:'0.8rem', padding:'3rem 1rem' },
  spin:    { fontSize:'2.2rem', opacity:0.35, animation:'none' },
  muted:   { fontSize:'0.85rem', color:'rgba(245,240,232,.5)', textAlign:'center', lineHeight:1.6 },
  hint:    { fontSize:'0.75rem', color:'rgba(245,240,232,.4)', textAlign:'center', margin:'0.8rem 0 0', lineHeight:1.5 },
}
